/**
 * Skin registry: single place to register packs. Add a new folder under `./<id>/`, export a
 * definition, append to {@link SKIN_ORDER}.
 */

import { isDevMode } from "../taskpane/env"
import { aiaSkin } from "./aia/skin"
import { credithqSkin } from "./credithq/skin"
import type { ExcelAddinSkinDefinition, ExcelAddinSkinId } from "./types"

/** Display / download order for browser landing manifest list. */
export const SKIN_ORDER: readonly ExcelAddinSkinDefinition[] = [aiaSkin, credithqSkin]

const BY_ID = new Map<ExcelAddinSkinId, ExcelAddinSkinDefinition>()
for (const skin of SKIN_ORDER) {
	BY_ID.set(skin.id, skin)
}

/** All manifest XML basenames allowed for download (dev + prod × each skin). */
export const MANIFEST_DOWNLOAD_FILENAMES = SKIN_ORDER.flatMap((s) => [s.manifest.dev, s.manifest.prod])

/** Allowed manifest basenames for `?download=1` and server routes. */
export const MANIFEST_BASENAME_RE = /^(?:manifest|prod-manifest)(?:-credithq)?\.xml$/

export function isAllowedManifestBasename(filename: string): boolean {
	const base = filename.replace(/^\//, "")
	return MANIFEST_BASENAME_RE.test(base)
}

function normalizeQueryParam(raw: string | null): ExcelAddinSkinId {
	if (raw?.toLowerCase() === credithqSkin.brandQueryValue) return "credithq"
	return "aia"
}

/**
 * Active skin id from `?brand=` (manifest-controlled). Defaults to AI Apollo.
 * Future: hosted JSON overrides or Office runtime context.
 */
export function getSkinId(): ExcelAddinSkinId {
	if (typeof window === "undefined") return "aia"
	return normalizeQueryParam(new URLSearchParams(window.location.search).get("brand"))
}

export function getActiveSkin(): ExcelAddinSkinDefinition {
	return BY_ID.get(getSkinId()) ?? aiaSkin
}

/** UI copy + id for taskpane / login. */
export type WhitelabelBrandConfig = ExcelAddinSkinDefinition["ui"] & { id: ExcelAddinSkinId }

export function getBrandConfig(): WhitelabelBrandConfig {
	const skin = getActiveSkin()
	return { id: skin.id, ...skin.ui }
}

/** @deprecated use getSkinId */
export function getExcelAddinBrand(): ExcelAddinSkinId {
	return getSkinId()
}

/** @deprecated prefer `!getActiveSkin().features.themeBuilderPage` */
export function isCreditHqBrand(): boolean {
	return getSkinId() === "credithq"
}

export function getManifestDownloadPairs(): { filename: string; label: string }[] {
	const dev = isDevMode()
	return SKIN_ORDER.map((skin) => ({
		filename: dev ? skin.manifest.dev : skin.manifest.prod,
		label: `${skin.ui.displayName} (${dev ? "dev" : "production"})`,
	}))
}

export function skinHasLockedTheme(
	skin: ExcelAddinSkinDefinition,
): skin is ExcelAddinSkinDefinition & { lockedTheme: NonNullable<ExcelAddinSkinDefinition["lockedTheme"]> } {
	return skin.lockedTheme != null
}
